import { cn } from "@/components/podkit/lib/cn";
import { Text } from "@/components/podkit/typography/Text";
import type { View } from "@/routes/insights/insights";
import * as TabsPrimitive from "@radix-ui/react-tabs";
import { IconArrowDownRight } from "central-icons/IconArrowDownRight";
import { IconArrowUpRight } from "central-icons/IconArrowUpRight";
import type { FC } from "react";

type Props = {
    title: string;
    value: string;
    trend: number;
    targetView: View;
    isLoading?: boolean;
};
export const UsageCard: FC<Props> = ({ title, value, trend, isLoading, targetView }) => {
    return (
        <TabsPrimitive.Trigger
            className={cn(
                "flex w-full flex-col gap-1 rounded-xl bg-surface-glass px-4 py-3 text-left focus:outline-none focus-visible:outline-2 focus-visible:outline-offset-[3px] md:w-auto md:max-w-[350px] md:flex-1",
                "focus-visible:outline-content-brand", // not using a ring here because that would override the shadow.
                "data-[state=active]:shadow-[0_0_0_1.5px] data-[state=inactive]:shadow-[0_0_0_0.5px] data-[state=active]:shadow-surface-button-primary-accent data-[state=inactive]:shadow-border-base", // We use a shadow instead of a border to not cause layout shift.
                "transition-colors duration-100 hover:bg-surface-hover",
            )}
            value={targetView}
            aria-label={`View ${title} chart. Current value: ${value}${!isLoading && trend !== 0 ? `, trending ${trend >= 0 ? "up" : "down"} ${(trend * 100).toFixed(0)}%` : ""}`}
            data-track-disable="true" // this is not very useful for our analytics
        >
            <Text className="text-sm font-bold">{title}</Text>
            <div className="flex flex-row items-baseline gap-2">
                <Text className="text-2xl font-bold">{value}</Text>
                {!isLoading && (
                    <Text
                        className={cn("flex flex-row items-end gap-1 text-base", {
                            "text-content-destructive": trend < 0,
                            "text-content-positive": trend > 0,
                        })}
                    >
                        <span className="sr-only">
                            Trending <span>{trend >= 0 ? "up" : "down"}</span>
                        </span>
                        {(trend * 100).toFixed(0)}%
                        {trend >= 0 ? (
                            <IconArrowUpRight className="size-3.5 self-center" />
                        ) : (
                            <IconArrowDownRight className="size-3.5 self-center" />
                        )}
                    </Text>
                )}
            </div>
        </TabsPrimitive.Trigger>
    );
};
